/**
 * ══ CẮT GIÁ VỐN Ở TẦNG TUẦN TỰ HOÁ (E16, B5 chốt 03/09) ═══════════════════════════════════════
 * Nguồn: specs/D03-danh-tinh-phan-quyen.md §4.3.1.
 * Lược đồ đầu ra PHỤ THUỘC QUYỀN: với người không có `seeVendorCost`, ba trường giá vốn
 * KHÔNG ĐƯỢC SINH RA trong lược đồ — không phải sinh ra rồi lọc/ẩn ở giao diện. Bản cũ gửi
 * xuống trình duyệt rồi mới ẩn ⇒ mở DevTools ▸ Network là đọc được.
 */
import { z } from 'zod'
import { isMoney, toMoney } from './money.ts'

/** Quyền liên quan tới đầu ra đơn hàng. */
export type Perms = { readonly seeVendorCost: boolean }

// NUMERIC(20,2) ở CSDL: tối đa 18 chữ số phần nguyên, tối đa 2 chữ số thập phân. Đây là ràng buộc
// RIÊNG của biên API (không phải chép lại DEC_RE của money.ts — DEC_RE lỏng hơn, không giới hạn
// số chữ số). Thiếu biên này, revenue/vendorCost 25 chữ số qua lọt zod rồi mới vỡ khi ghi CSDL.
const NUMERIC_20_2_RE = /^-?\d{1,18}(\.\d{1,2})?$/

// Lược đồ tiền cho zod — DÙNG LẠI isMoney của money.ts (không chép lại biểu thức chính quy của nó),
// siết thêm cho khớp NUMERIC(20,2), rồi CHUẨN HOÁ qua toMoney() (không phải ép kiểu suông
// `as Money`) — nếu không, `orderOut(...).parse({ revenue: '100' })` trả về `'100'` đã mang nhãn
// `Money` mà KHÔNG có đúng 2 chữ số thập phân, phá bất biến mà toMoney() bảo đảm cho mọi Money
// khác trong hệ thống (so sánh chuỗi, khoá gom nhóm, hiển thị đều lệch).
const money = z
  .string()
  .refine(isMoney, { message: 'Không phải Money hợp lệ' })
  .refine((v) => NUMERIC_20_2_RE.test(v), {
    message: 'Tiền vượt phạm vi CSDL NUMERIC(20,2): tối đa 18 chữ số phần nguyên, 2 chữ số thập phân',
  })
  .transform((v) => toMoney(v))

// Hai lược đồ ĐẦY ĐỦ, TÁCH RIÊNG — không phải một object literal với spread có điều kiện
// (`...(can.seeVendorCost ? {...} : {})`). Mẫu spread-trong-một-literal khiến TypeScript suy ra
// MỘT kiểu tĩnh DUY NHẤT cho cả hai nhánh (ba trường giá vốn thành `optional unknown` bất kể
// `can` là gì) — ở TẦNG KIỂU, E16 không phân biệt được hai nhánh, chỉ đảm bảo đúng lúc CHẠY.
// Viết thành hai lược đồ riêng để TypeScript suy ra hai kiểu THẬT SỰ khác nhau (xem overload dưới).
const buildWithVendorCost = () =>
  z.object({
    id: z.string(),
    ref: z.string(),
    revenue: money,
    vendorCost: money,
    grossMargin: money,
    vendorPrice: money,
  })

const buildWithoutVendorCost = () =>
  z.object({
    id: z.string(),
    ref: z.string(),
    revenue: money,
  })

/**
 * Lược đồ đầu ra đơn hàng, PHỤ THUỘC quyền `can`. Khi `seeVendorCost: false`, ba khoá
 * `vendorCost` · `grossMargin` · `vendorPrice` KHÔNG TỒN TẠI trong lược đồ (không sinh ra rồi lọc)
 * — đúng ở CẢ HAI TẦNG: lúc chạy (test) và lúc biên dịch (overload dưới đây, đo bằng `tsc`).
 *
 * Ba overload: hai overload đầu cho gọi với literal `{ seeVendorCost: true }` /
 * `{ seeVendorCost: false }` (như trong test) ra đúng kiểu riêng của nhánh đó; overload thứ ba
 * (nhận `Perms` chung, `seeVendorCost: boolean` xác định lúc CHẠY — trường hợp thật của phần lớn
 * route trong 21 Task sau) trả về hợp của cả hai kiểu, buộc bên gọi phải tự thu hẹp (kiểm
 * `can.seeVendorCost` hoặc `'vendorCost' in result`) trước khi đọc trường giá vốn.
 */
export function orderOut(can: { readonly seeVendorCost: true }): ReturnType<typeof buildWithVendorCost>
export function orderOut(can: { readonly seeVendorCost: false }): ReturnType<typeof buildWithoutVendorCost>
export function orderOut(
  can: Perms,
): ReturnType<typeof buildWithVendorCost> | ReturnType<typeof buildWithoutVendorCost>
export function orderOut(
  can: Perms,
): ReturnType<typeof buildWithVendorCost> | ReturnType<typeof buildWithoutVendorCost> {
  if (can.seeVendorCost) return buildWithVendorCost()
  return buildWithoutVendorCost()
}
